'use client'

import { featureFlagsAtom } from '@polaris/core/stores'
import { formatCoin, formatPrice } from '@polaris/core/utils'
import { useAtomValue, useSetAtom } from 'jotai'
import Link from 'next/link'
import { useMemo } from 'react'
import { useSelectedTickerInfo } from '@/src/hooks/token-info/use-selected-ticker'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { useAccountsFilteredBalances } from '@/src/queries/hooks/balances'
import {
  onboardingDialogAtom,
  openOnboardingDialog,
} from '@/src/stores/onboarding'
import { cn } from '@/src/utils/styles'
import { BalanceListRowButton, MultiBalancesImage } from '../balances'
import { TradeModalDialog } from '../trade-modal'
import { Button } from '../ui/button'
import { Chevron } from '../ui/icons/chevron'
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet'

export default function TradingSheet() {
  const { ticker } = useSelectedTickerInfo()
  const { balancesSummary: balances } = useAccountsFilteredBalances({
    ticker: ticker ?? undefined,
  })

  const featureFlags = useAtomValue(featureFlagsAtom)
  const setOnboardingDialog = useSetAtom(onboardingDialogAtom)

  const { logos, numBalances, totalAmount, usdValue } = useMemo(() => {
    const balance = balances?.[0] ? balances[0].summary : null
    return {
      logos: balance?.unique_logo_img_urls,
      numBalances: balance?.tickers.length,
      usdValue: balance?.value_usd,
      totalAmount: balance?.normalized_amount,
    }
  }, [balances])

  const {
    close: closeSheet,
    isOpen: isSheetOpen,
    open: openSheet,
  } = useDisclosure()

  const {
    close: closeTradingModal,
    isOpen: isTradingModalOpen,
    open: openTradingModal,
  } = useDisclosure()

  return (
    <div
      className={cn(
        'flex fixed md:hidden pt-3 px-4 pb-2 md:pb-4 md:px-8 h-[84px] md:h-[124px] bottom-0 left-0  w-full z-20 transition-all duration-300',
        {
          'from-surface1-100 to-surface1-100': isSheetOpen,
          'bg-gradient-to-b from-transparent to-background to-30%':
            balances.length > 0,
        },
      )}
    >
      <div className='flex items-center justify-end w-full md:py-2 md:px-4'>
        {balances.length > 0 && (
          <Sheet open={isSheetOpen}>
            <SheetTrigger onClick={openSheet} className='w-full'>
              <div className='flex items-center gap-3 md:gap-4 pr-4 py-2 w-full'>
                {isSheetOpen ? (
                  <Button
                    className='size-6 bg-surface1 rounded-full md:size-8 w-auto'
                    squared
                    onClick={closeSheet}
                  >
                    <Chevron className='size-6 min-w-6 text-secondary rotate-180' />
                  </Button>
                ) : (
                  logos &&
                  numBalances && (
                    <MultiBalancesImage
                      logoUrls={logos}
                      numBalances={numBalances}
                      className='size-10 md:size-12'
                    />
                  )
                )}
                <div className='flex flex-col text-left'>
                  <span className='text-sm md:text-base font-semibold text-primary'>
                    {formatPrice(usdValue ?? 0)}
                  </span>
                  <span className='text-xs md:text-sm text-tertiary'>
                    {formatCoin({
                      amount: totalAmount ?? 0,
                      denom: ticker?.toUpperCase() ?? '',
                    })}
                  </span>
                </div>
              </div>
            </SheetTrigger>
            <SheetContent
              side='bottom'
              onSheetClick={closeSheet}
              className='pt-3 px-1 bottom-20 md:bottom-[124px]'
            >
              <div className='flex items-center px-4 py-1.5'>
                <span className='text-primary text-sm font-semibold'>
                  Balances
                </span>
              </div>
              <div className='flex flex-col w-full'>
                {balances?.[0].balances.map((balance) => {
                  return (
                    <BalanceListRowButton key={balance.denom} {...balance} />
                  )
                })}
              </div>
            </SheetContent>
          </Sheet>
        )}
        <Button
          variant='primary'
          className={cn('hidden sm:inline-flex transition-opacity', {
            'opacity-0': isSheetOpen,
          })}
          onClick={openTradingModal}
        >
          Trade
        </Button>
        <Button
          variant='primary'
          className='shadow-lg sm:hidden'
          onClick={(e) => {
            if (featureFlags.newLoginFlow) {
              e.preventDefault()
              setOnboardingDialog(openOnboardingDialog())
            }
          }}
          asChild={!featureFlags.newLoginFlow}
        >
          {featureFlags.newLoginFlow ? (
            'Trade'
          ) : (
            <Link href='/login'>Trade</Link>
          )}
        </Button>
      </div>
      <TradeModalDialog
        isOpen={isTradingModalOpen}
        open={openTradingModal}
        close={closeTradingModal}
      />
    </div>
  )
}
